// src/components/landing/ComparisonTable.tsx
'use client'

import Link from 'next/link'
import { CheckCircle2, XCircle } from 'lucide-react'
import {
  COMPETITOR_PRICING,
  competitorCheckedOnLabel,
  competitorPriceCell,
  competitorPricing,
} from '@/lib/marketing/competitor-pricing'
import { PRO_TERMS } from '@/lib/billing/plan-config'

type Cell = boolean | string

// Competitor columns: Khan Academy, Acely, OnePrep, Princeton Review.
type Row = {
  feature: string
  us: Cell
  ka: Cell // Khan Academy
  ac: Cell // Acely
  op: Cell // OnePrep
  pr: Cell // Princeton Review
}

// Values are kept honest + defensible: every competitor cell is a feature or a
// price a visitor can check on the competitor's own site. We do NOT compare how
// anyone checks their explanations — we can't see inside a rival's process, and
// a ✗ there would be an unsupported claim about them (that row was removed; our
// own checking is described on /quality, linked below the table). The "Free to
// start" and Price cells come from competitor-pricing.ts, which records the
// exact plan, upfront charge, billing period and the day each was checked —
// shown under the table (external QA: prices had no billing basis or date, and
// Acely's free version was marked ✗).
const KA = competitorPricing('ka')
const AC = competitorPricing('ac')
const OP = competitorPricing('op')
const PR = competitorPricing('pr')

const ROWS: Row[] = [
  {
    feature: 'Free to start',
    us: true,
    ka: KA.hasFreeAccess,
    ac: AC.hasFreeAccess,
    op: OP.hasFreeAccess,
    pr: PR.hasFreeAccess,
  },
  {
    feature: 'Unlimited full-length practice tests',
    us: true,
    ka: false,
    ac: true,
    op: true,
    pr: false,
  },
  {
    feature: 'Adaptive, AI-personalized practice',
    us: true,
    ka: false,
    ac: true,
    op: true,
    pr: false,
  },
  { feature: 'AI tutor on any question', us: true, ka: true, ac: true, op: true, pr: false },
  { feature: 'Spaced-repetition review', us: true, ka: false, ac: false, op: false, pr: false },
  { feature: 'Personalized study plan', us: true, ka: false, ac: true, op: true, pr: true },
  {
    feature: 'Price',
    us: 'Free–$24.99/mo',
    ka: competitorPriceCell(KA),
    ac: competitorPriceCell(AC),
    op: competitorPriceCell(OP),
    pr: competitorPriceCell(PR),
  },
]

const COLUMNS: { key: keyof Omit<Row, 'feature'>; label: string }[] = [
  { key: 'us', label: 'UnlimitedTests' },
  { key: 'ka', label: 'Khan Academy' },
  { key: 'ac', label: 'Acely' },
  { key: 'op', label: 'OnePrep' },
  { key: 'pr', label: 'Princeton Review' },
]

function CellView({ value, highlight = false }: { value: Cell; highlight?: boolean }) {
  if (typeof value === 'boolean') {
    // Green ✓ vs rose ✗ — the universal yes/no read, so the two don't blur
    // together in light mode (they used to both be dark: emerald check +
    // slate-600 cross). The "us" column is highlighted by its bg tint, not the
    // icon colour, so all checks share one vivid green.
    return value ? (
      <CheckCircle2
        className="mx-auto h-5 w-5 text-emerald-600 dark:text-emerald-400"
        aria-label="Included"
      />
    ) : (
      <XCircle
        className="mx-auto h-5 w-5 text-rose-400 dark:text-rose-500"
        aria-label="Not included"
      />
    )
  }
  return (
    <span
      className={`text-sm ${highlight ? 'font-semibold text-brand-ink' : 'text-muted-foreground'}`}
    >
      {value}
    </span>
  )
}

export default function ComparisonTable() {
  return (
    <section
      id="compare"
      className="scroll-mt-20 border-y border-border/60 bg-card/30 px-5 py-14 md:px-10"
    >
      <div className="mx-auto max-w-7xl">
        <h2 className="mb-3 text-center text-2xl font-semibold text-foreground md:text-3xl">
          How we compare
        </h2>
        <p className="mb-10 text-center text-sm text-muted-foreground">
          Explanations checked before you see them and a real AI tutor — at a fraction of the price.
        </p>

        <div className="overflow-x-auto rounded-2xl border border-border bg-card/60">
          <table className="w-full min-w-[760px] border-collapse text-left">
            <thead>
              <tr className="border-b border-border">
                <th
                  scope="col"
                  className="px-4 py-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:px-6"
                >
                  Feature
                </th>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className={`px-4 py-4 text-center text-xs font-semibold uppercase tracking-wider sm:px-6 ${
                      c.key === 'us' ? 'bg-brand/10 text-brand-ink' : 'text-muted-foreground'
                    }`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, i) => (
                <tr
                  key={row.feature}
                  className={i !== ROWS.length - 1 ? 'border-b border-border/70' : ''}
                >
                  <th scope="row" className="px-4 py-4 text-sm font-medium text-foreground sm:px-6">
                    {row.feature}
                  </th>
                  {COLUMNS.map((c) => (
                    <td
                      key={c.key}
                      className={`px-4 py-4 text-center sm:px-6 ${c.key === 'us' ? 'bg-brand/10' : ''}`}
                    >
                      <CellView value={row[c.key]} highlight={c.key === 'us'} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* The billing basis behind every price, and when we checked it. */}
        <div
          className="mt-6 rounded-2xl border border-border bg-card/40 p-4 text-xs leading-relaxed text-muted-foreground sm:p-6"
          data-testid="competitor-prices"
        >
          <p className="font-medium text-foreground">
            What each price means (checked on {competitorCheckedOnLabel()})
          </p>
          <ul className="mt-3 space-y-3">
            <li>
              <span className="font-medium text-foreground">UnlimitedTests</span>: Free plan. Pro is{' '}
              {PRO_TERMS.monthly.totalLabel}, {PRO_TERMS.sixmo.totalLabel}, or{' '}
              {PRO_TERMS.annual.totalLabel}.
            </li>
            {COMPETITOR_PRICING.map((c) => (
              <li key={c.key}>
                <span className="font-medium text-foreground">{c.name}</span>:{' '}
                {c.verified ? (
                  <>
                    {c.freeAccess}
                    {c.plans.length > 0 && (
                      <>
                        {' '}
                        Paid:{' '}
                        {c.plans.map((p, i) => (
                          <span key={p.name}>
                            {i > 0 ? '; ' : ''}
                            {p.name} {p.upfront}, {p.billing}
                          </span>
                        ))}
                        .
                      </>
                    )}
                    {c.note ? <> {c.note}</> : null}
                  </>
                ) : (
                  <>Not verified — we couldn&apos;t check this page on that date.</>
                )}{' '}
                Source:{' '}
                <a
                  href={c.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  {c.sourceLabel}
                </a>
                .
              </li>
            ))}
          </ul>
          <p className="mt-3">
            Competitors change their prices; check their sites for today&apos;s offer.
          </p>
        </div>

        {/* Back the "explanations checked before you see them" line with the
            page that explains how (roadmap 1.1 — the keystone trust wedge). */}
        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link
            href="/quality"
            className="font-medium text-brand-ink underline-offset-4 hover:underline dark:text-brand-ink"
          >
            See how explanations are checked →
          </Link>
        </p>
      </div>
    </section>
  )
}
